import { PaymentCalculationRule, PaymentRule } from "types/payment";
import { TimeSlot } from "types/time-slot";
import { parseDate } from "utils/moment-utils";

export class WebRule {
    id: number;
    sectionId: number;
    title: string;
    bookingType: WebRuleBookingType;
    conditions: string;
    paymentInformation: string;
    maxGroupSize: number;
    minGroupSize: number;
    recoupTime: number;
    isClosed: boolean;
    date: IMoment;
    times: TimeSlot[];
    menuGuid: string;
    requireMenuSelection: boolean;
    requireMenuPayment: boolean;

    startTime: number;
    lockTime: number;
    lockPerTime: boolean;

    /* Duplicates to map from backend */
    readonly payment: PaymentRule;
    readonly paymentModifier: PaymentCalculationRule;
    /* Frontend mapped named */
    paymentRule: PaymentRule;
    paymentCalculationRule: PaymentCalculationRule;

    paymentAmount: number;
    paymentVAT: number;
    isTextRow: boolean;
    comment: string;
    archived?: string;

    experienceId?: number;
    experienceName?: { [key: string]: string };
    experienceImage1x1?: string;
    experienceBookingConditions?: string;
    maximumGapTime?: number;

    constructor(values?: Partial<WebRule>) {
        this.id = values?.id ?? 0;
        this.sectionId = values?.sectionId ?? 0;
        this.date = parseDate(values?.date);
        this.title = values?.title ?? "";
        this.conditions = values?.conditions ?? "";
        this.bookingType = values?.bookingType ?? WebRuleBookingType.Unknown;
        this.paymentInformation = values?.paymentInformation ?? "";
        // Max group size is used for including/excluding what rule to look at for the current group. It's a filter.
        this.maxGroupSize = values?.maxGroupSize ?? 0;
        // Min group size is used for including/excluding what rule to look at for the current group. It's a filter.
        this.minGroupSize = values?.minGroupSize ?? 0;
        this.recoupTime = values?.recoupTime ?? 0;
        this.isClosed = values?.isClosed ?? false;
        this.times = values?.times?.map((time) => new TimeSlot(time)) ?? [];
        this.menuGuid = values?.menuGuid ?? "";
        this.requireMenuSelection = values?.requireMenuSelection ?? false;
        this.requireMenuPayment = values?.requireMenuPayment ?? false;
        this.startTime = values?.startTime ?? 0;
        this.lockTime = values?.lockTime ?? 0;
        this.lockPerTime = values?.lockPerTime ?? false;
        this.payment = values?.payment ?? PaymentRule.NoPayment;
        this.paymentModifier = values?.paymentModifier ?? PaymentCalculationRule.NoPayment;
        this.paymentRule = values?.payment ?? PaymentRule.NoPayment;
        this.paymentCalculationRule = values?.paymentModifier ?? PaymentCalculationRule.NoPayment;
        this.paymentAmount = values?.paymentAmount ?? 0;
        this.paymentVAT = values?.paymentVAT ?? 0;
        this.isTextRow = values?.isTextRow ?? false;
        this.comment = values?.comment ?? "";
        this.archived = values?.archived;
        this.experienceId = values?.experienceId;
        this.experienceName = values?.experienceName;
        this.experienceImage1x1 = values?.experienceImage1x1;
        this.experienceBookingConditions = values?.experienceBookingConditions;
        this.maximumGapTime = values?.maximumGapTime;
    }

    clone(): WebRule {
        return new WebRule(this);
    }

    public hasAnyPaymentRule(): boolean {
        return (
            this.paymentRule === PaymentRule.Prepayment ||
            this.paymentRule === PaymentRule.NoShowFee
        );
    }

    public hasNoShowRule(): boolean {
        return this.paymentRule === PaymentRule.NoShowFee;
    }

    public hasPrepaymentRule(): boolean {
        return this.paymentRule === PaymentRule.Prepayment;
    }

    public requiresAnyPayment(): boolean {
        return this.hasAnyPaymentRule() || this.requireMenuPayment;
    }
}

export enum WebRuleBookingType {
    Booking = 0,
    Takeaway = 1,
    Delivery = 2,
    Request = 3,
    Experience = 4,

    Unknown = 99,
}
